import { useEffect, useState } from "react";
import { api } from "../api";
import { navigate } from "../router";
import type { FactCategory, Memory as MemoryData, MemoryFact } from "../types";
import { IconBin, IconDoc, IconPencil } from "./Icons";

/**
 * Memorie: what the assistant keeps about the user and their own firm,
 * every line of it editable.
 *
 * Four sources, one list, told apart by a dot: written by the user, read
 * from the compliance profile, taken from a document, or inferred by the
 * assistant. The last two are proposals - shown with a blue edge and a
 * "Corect" button, and not used in any answer until confirmed. That is the
 * line the backend holds (app/memory.py); this screen is where the user
 * walks it.
 */

export type MemoryTab = FactCategory | "files";

const TABS: { id: MemoryTab; label: string; to: string }[] = [
  { id: "company", label: "Firma", to: "/memory" },
  { id: "people", label: "Oameni și roluri", to: "/memory/people" },
  { id: "preferences", label: "Preferințe", to: "/memory/preferences" },
  { id: "files", label: "Fișiere", to: "/memory/files" },
];

const DOT: Record<MemoryFact["source"], string> = {
  user: "d-me",
  profile: "d-prof",
  document: "",
  inferred: "d-ai",
};

function day(iso: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("ro-MD", { day: "numeric", month: "short" });
}

export function Memory({ tab }: { tab: MemoryTab }) {
  const [data, setData] = useState<MemoryData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pendingOnly, setPendingOnly] = useState(false);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .memory()
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : "Eroare"));
  }, []);

  async function run(work: () => Promise<MemoryData>): Promise<boolean> {
    setBusy(true);
    try {
      setData(await work());
      setError(null);
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : "Eroare");
      return false;
    } finally {
      setBusy(false);
    }
  }

  if (!data) {
    return (
      <div className="page memory">
        <div className="page-body">
          {error ? <p className="error" role="alert">{error}</p> : <p className="muted">se încarcă…</p>}
        </div>
      </div>
    );
  }

  const pending = data.facts.filter((f) => !f.confirmed);
  const files = groupFiles(data.facts);
  const counts: Record<MemoryTab, number> = {
    company: data.facts.filter((f) => f.category === "company").length,
    people: data.facts.filter((f) => f.category === "people").length,
    preferences: data.facts.filter((f) => f.category === "preferences").length,
    files: files.length,
  };
  const facts = tab === "files"
    ? []
    : data.facts.filter((f) => f.category === tab && (!pendingOnly || !f.confirmed));

  return (
    <div className="page memory">
      <div className="page-top">
        <div className="page-top-row">
          <h1>Ce reține asistentul</h1>
          <p className="sub">Folosit în răspunsuri, documente și conformitate. Corectați oricând.</p>
          <button
            className={data.enabled ? "switch on" : "switch"}
            role="switch"
            aria-checked={data.enabled}
            disabled={busy}
            onClick={() => void run(() => api.setMemoryEnabled(!data.enabled))}
          >
            Memorie {data.enabled ? "activă" : "oprită"}
            <span className="knob" aria-hidden="true"><i /></span>
          </button>
        </div>
        <div className="ttabs" role="tablist" aria-label="Categorii">
          {TABS.map((t) => (
            <button
              key={t.id}
              role="tab"
              aria-selected={tab === t.id}
              className={tab === t.id ? "on" : undefined}
              onClick={() => {
                setAdding(false);
                navigate(t.to);
              }}
            >
              {t.label} <span className="cnt">{counts[t.id]}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="page-body" key={tab}>
        <div className="mem-inner">
          {error && <p className="error" role="alert">{error}</p>}

          {!data.enabled && (
            <p className="paused">
              Memoria e oprită: asistentul nu reține nimic nou și nu folosește ce e mai jos.
            </p>
          )}

          {pending.length > 0 && (
            <div className="pending">
              <strong>
                {pending.length === 1
                  ? "Un lucru dedus de asistent așteaptă confirmarea"
                  : `${pending.length} lucruri deduse de asistent așteaptă confirmarea`}
                <span className="pending-why"> — până atunci nu sunt folosite în răspunsuri.</span>
              </strong>
              <button className="btn small" disabled={busy} onClick={() => void run(() => api.confirmFacts())}>
                Confirmă toate
              </button>
            </div>
          )}

          {tab !== "files" && (
            <>
              <div className="toolbar">
                <button className={!pendingOnly ? "chipbtn on" : "chipbtn"} onClick={() => setPendingOnly(false)}>
                  Toate
                </button>
                <button className={pendingOnly ? "chipbtn on" : "chipbtn"} onClick={() => setPendingOnly(true)}>
                  De confirmat
                </button>
                <button className="btn small ml" onClick={() => setAdding(true)} disabled={adding}>
                  + Adaugă
                </button>
              </div>

              <div className="facts">
                {adding && (
                  <AddRow
                    busy={busy}
                    onCancel={() => setAdding(false)}
                    onSave={async (label, value) => {
                      if (await run(() => api.addFact({ category: tab, label, value }))) setAdding(false);
                    }}
                  />
                )}
                {facts.map((f) => (
                  <FactRow
                    key={f.id}
                    fact={f}
                    busy={busy}
                    onConfirm={() => void run(() => api.confirmFacts([f.id]))}
                    onSave={(value) => run(() => api.updateFact(f.id, value))}
                    onDelete={() => void run(() => api.deleteFact(f.id))}
                  />
                ))}
                {!adding && facts.length === 0 && (
                  <p className="empty">
                    {pendingOnly
                      ? "Nimic de confirmat aici."
                      : tab === "company"
                        ? "Nimic reținut încă. Răspunsurile din profilul de conformitate apar aici, la fel ca ce adăugați dvs."
                        : "Nimic reținut încă."}
                  </p>
                )}
              </div>

              <div className="legend">
                <span><i className="srcdot d-me" />scris de dvs.</span>
                <span><i className="srcdot d-prof" />din profilul de conformitate</span>
                <span><i className="srcdot" />din documente</span>
                <span><i className="srcdot d-ai" />dedus de asistent</span>
              </div>
            </>
          )}

          {tab === "files" &&
            (files.length === 0 ? (
              <p className="empty">Niciun fișier reținut.</p>
            ) : (
              <div className="files">
                {files.map((f) => (
                  <FileCard
                    key={f.ref}
                    file={f}
                    busy={busy}
                    onForget={() => void run(() => api.forgetFile(f.ref))}
                  />
                ))}
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

interface FileGroup {
  ref: string;
  name: string;
  since: string | null;
  labels: string[];
}

function groupFiles(facts: MemoryFact[]): FileGroup[] {
  const by = new Map<string, FileGroup>();
  for (const f of facts) {
    if (f.source !== "document" || !f.source_ref) continue;
    let g = by.get(f.source_ref);
    if (!g) {
      g = { ref: f.source_ref, name: f.file_name ?? "fișier", since: f.created_at, labels: [] };
      by.set(f.source_ref, g);
    }
    g.labels.push(f.label);
  }
  return [...by.values()];
}

function FactRow({
  fact, busy, onConfirm, onSave, onDelete,
}: {
  fact: MemoryFact;
  busy: boolean;
  onConfirm: () => void;
  onSave: (value: string) => Promise<boolean>;
  onDelete: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(fact.value);
  // Two-click delete: the bin, then "Ștergeți?". Leaving the row cancels.
  const [askDelete, setAskDelete] = useState(false);
  const guess = !fact.confirmed;
  const fromProfile = fact.source === "profile";

  const cls = ["fact", guess ? "guess" : "", editing ? "editing" : ""].filter(Boolean).join(" ");

  return (
    <div className={cls} onMouseLeave={() => setAskDelete(false)}>
      <span className="fk">{fact.label}</span>
      {editing ? (
        <div className="fv">
          <div className="edit">
            <label className="sr-only" htmlFor={`in-${fact.id}`}>{fact.label}</label>
            <input
              id={`in-${fact.id}`}
              value={text}
              autoFocus
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === "Escape" && setEditing(false)}
            />
            <button
              className="btn small primary"
              disabled={busy || !text.trim()}
              onClick={async () => {
                if (await onSave(text.trim())) setEditing(false);
              }}
            >
              Salvează
            </button>
            <button className="btn small" onClick={() => { setText(fact.value); setEditing(false); }}>
              Renunță
            </button>
          </div>
          {fromProfile && (
            <span className="fsrc">Se schimbă și în profilul de conformitate, și poate muta obligațiile.</span>
          )}
          {guess && !fromProfile && (
            <span className="fsrc">Corectat de dvs., faptul devine al dvs. și e folosit de acum.</span>
          )}
        </div>
      ) : (
        <>
          <div className="fv">
            <span className="val">{fact.value}</span>
            <span className="fsrc">
              <i className={`srcdot ${DOT[fact.source]}`} />
              {fact.source_note}
            </span>
          </div>
          <div className="fa">
            {guess && (
              <button className="btn small primary" disabled={busy} onClick={onConfirm}>Corect</button>
            )}
            <button
              className="iconb"
              aria-label={`Editează ${fact.label}`}
              onClick={() => { setText(fact.value); setEditing(true); }}
            >
              <IconPencil size={18} />
            </button>
            {askDelete ? (
              <button
                className="btn small danger"
                disabled={busy}
                onClick={onDelete}
                title={fromProfile ? "Șterge și răspunsul din profilul de conformitate" : undefined}
              >
                Ștergeți?
              </button>
            ) : (
              <button className="iconb" aria-label={`Șterge ${fact.label}`} onClick={() => setAskDelete(true)}>
                <IconBin size={18} />
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function AddRow({
  busy, onSave, onCancel,
}: {
  busy: boolean;
  onSave: (label: string, value: string) => void;
  onCancel: () => void;
}) {
  const [label, setLabel] = useState("");
  const [value, setValue] = useState("");
  const ok = label.trim() && value.trim();
  return (
    <div className="fact editing">
      <div className="fv">
        <div className="edit">
          <input
            aria-label="Ce anume"
            placeholder="Ce anume (ex. Sediul)"
            value={label}
            autoFocus
            onChange={(e) => setLabel(e.target.value)}
          />
          <input
            aria-label="Valoarea"
            placeholder="Valoarea"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && ok && onSave(label.trim(), value.trim())}
          />
          <button className="btn small primary" disabled={busy || !ok} onClick={() => onSave(label.trim(), value.trim())}>
            Salvează
          </button>
          <button className="btn small" onClick={onCancel}>Renunță</button>
        </div>
        <span className="fsrc">
          Despre dvs. sau firma dvs. Nu adăugați date despre clienți — aparțin
          secretului profesional al clientului.
        </span>
      </div>
    </div>
  );
}

function FileCard({
  file, busy, onForget,
}: {
  file: FileGroup;
  busy: boolean;
  onForget: () => void;
}) {
  const [ask, setAsk] = useState(false);
  return (
    <div className="file" onMouseLeave={() => setAsk(false)}>
      <div className="file-top">
        <span className="fileic" aria-hidden="true"><IconDoc /></span>
        <div>
          <strong title={file.name}>{file.name}</strong>
          <span>Reținut {day(file.since)}</span>
        </div>
      </div>
      <div className="learned">
        {file.labels.map((l) => <span key={l}>{l}</span>)}
      </div>
      {ask ? (
        <button className="btn small danger" disabled={busy} onClick={onForget}>
          Uitați tot din acest fișier?
        </button>
      ) : (
        <button className="btn small danger" onClick={() => setAsk(true)}>Uită fișierul</button>
      )}
    </div>
  );
}
